import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const CertificationGenerator = () => {
  const [selectedTemplate, setSelectedTemplate] = useState('standard');

  const certifications = [
    {
      id: 1,
      employee: 'John Doe',
      certification: 'Forklift Operator Certification',
      issueDate: '12/01/2025',
      expiryDate: '12/01/2026',
      score: 92,
      status: 'active',
      certificateNumber: 'FO-2025-001'
    },
    {
      id: 2,
      employee: 'Sarah Johnson',
      certification: 'Safety Orientation',
      issueDate: '11/28/2025',
      expiryDate: 'Lifetime',
      score: 88,
      status: 'active',
      certificateNumber: 'SO-2025-045'
    },
    {
      id: 3,
      employee: 'Mike Chen',
      certification: 'Hazardous Materials Handler',
      issueDate: '10/15/2024',
      expiryDate: '10/15/2025',
      score: 85,
      status: 'expiring-soon',
      certificateNumber: 'HM-2024-128'
    }
  ];

  const templates = [
    { id: 'standard', name: 'Standard Certificate', preview: 'https://via.placeholder.com/400x300' },
    { id: 'professional', name: 'Professional Certificate', preview: 'https://via.placeholder.com/400x300' },
    { id: 'modern', name: 'Modern Certificate', preview: 'https://via.placeholder.com/400x300' }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-500/10 text-green-500 border-green-500/20';
      case 'expiring-soon':
        return 'bg-orange-500/10 text-orange-500 border-orange-500/20';
      case 'expired':
        return 'bg-red-500/10 text-red-500 border-red-500/20';
      default:
        return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Left Panel - Certificate Designer */}
      <div className="lg:col-span-1 space-y-6">
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Certificate Designer</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Certificate Template
              </label>
              <select 
                value={selectedTemplate}
                onChange={(e) => setSelectedTemplate(e?.target?.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {templates?.map((template) => (
                  <option key={template?.id} value={template?.id}>
                    {template?.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Organization Name
              </label>
              <input
                type="text"
                placeholder="Your Organization"
                defaultValue="Argus Safety Solutions"
                className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Authorized Signatory
              </label>
              <input
                type="text"
                placeholder="Name of Signatory"
                className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Signatory Title
              </label>
              <input
                type="text"
                placeholder="e.g., Safety Director"
                className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Upload Logo
              </label>
              <div className="border-2 border-dashed border-border rounded-lg p-4 text-center hover:border-primary transition-colors cursor-pointer">
                <Icon name="Upload" size={24} className="mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Click to upload logo</p>
              </div>
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-border">
              <span className="text-sm font-medium text-foreground">Enable Blockchain Verification</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" className="sr-only peer" />
                <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>

            <button className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium">
              Save Template
            </button>
          </div>
        </div>

        {/* Bulk Generation */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Bulk Generation</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Generate certificates for multiple employees who passed assessments
          </p>
          <button className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium">
            <Icon name="FileStack" size={18} />
            Generate Bulk Certificates
          </button>
        </div>
      </div>
      {/* Right Panel - Issued Certificates */}
      <div className="lg:col-span-2">
        <div className="bg-card border border-border rounded-lg">
          <div className="p-6 border-b border-border">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">Issued Certificates</h3>
              <div className="flex items-center gap-2">
                <button className="inline-flex items-center gap-2 px-4 py-2 bg-background border border-border rounded-lg hover:bg-muted transition-colors text-sm">
                  <Icon name="Filter" size={16} />
                  Filter
                </button>
                <button className="inline-flex items-center gap-2 px-4 py-2 bg-background border border-border rounded-lg hover:bg-muted transition-colors text-sm">
                  <Icon name="Download" size={16} />
                  Export
                </button>
              </div>
            </div>
          </div>

          <div className="divide-y divide-border">
            {certifications?.map((cert) => (
              <div key={cert?.id} className="p-6 hover:bg-muted/50 transition-colors">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon name="Award" size={24} color="var(--color-primary)" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-foreground">{cert?.employee}</h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(cert?.status)}`}>
                          {cert?.status}
                        </span>
                      </div>
                      <p className="text-sm text-foreground mb-1">{cert?.certification}</p>
                      <p className="text-xs text-muted-foreground">
                        Certificate #: {cert?.certificateNumber}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button className="p-2 hover:bg-background rounded-lg transition-colors" title="View Certificate">
                      <Icon name="Eye" size={18} />
                    </button>
                    <button className="p-2 hover:bg-background rounded-lg transition-colors" title="Download PDF">
                      <Icon name="Download" size={18} />
                    </button>
                    <button className="p-2 hover:bg-background rounded-lg transition-colors" title="Send Email">
                      <Icon name="Mail" size={18} />
                    </button>
                    <button className="p-2 hover:bg-background rounded-lg transition-colors" title="Verify">
                      <Icon name="Shield" size={18} />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Issue Date</span>
                    <p className="font-medium text-foreground">{cert?.issueDate}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Expiry Date</span>
                    <p className="font-medium text-foreground">{cert?.expiryDate}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Score</span>
                    <p className="font-medium text-foreground">{cert?.score}%</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Verification</span>
                    <div className="flex items-center gap-1 text-success">
                      <Icon name="CheckCircle" size={14} />
                      <span className="font-medium">Verified</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Empty State when no certificates */}
          {certifications?.length === 0 && (
            <div className="p-12 text-center">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Award" size={32} color="var(--color-muted-foreground)" />
              </div>
              <h4 className="text-lg font-semibold text-foreground mb-2">No Certificates Issued</h4>
              <p className="text-muted-foreground mb-4">
                Certificates will appear here once assessments are completed and passed
              </p>
            </div>
          )}
        </div>

        {/* Verification Panel */}
        <div className="bg-card border border-border rounded-lg p-6 mt-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Icon name="Shield" size={24} color="var(--color-primary)" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Certificate Verification</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Enable blockchain-based certificate verification for authenticity and tamper-proof records. Employers can verify certificates instantly using certificate numbers.
              </p>
              <button className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium">
                <Icon name="Settings" size={16} />
                Configure Verification
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CertificationGenerator;